/**
 * act-as-waap-account.ts — React Query hooks for WAAP account-level mutations (B3 batch).
 *
 * Covers: AI-WAF General (strategies/mode), AI-WAF Settings, Emergency Mitigation,
 * Programmable Mitigation (PBR). All via actAsClient (X-Act-As-Org header injected).
 *
 * WAAP responses are NOT enveloped — no .data unwrap; guard null on 400/404.
 * Mutation keys sit alongside query keys so ActAsMutationConfirm can invalidate them.
 */

import { useQuery } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';

/* ── Shared null-guard helper ────────────────────────────────────────────── */

function isNullStatus(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 400 || status === 404 || status === 501;
}

/* ─────────────────────────────────────────────────────────────────────────
   AI-WAF General (B4 spec) — strategies catalog + mode
   ────────────────────────────────────────────────────────────────────────── */

export interface WafStrategyRow {
  strategy_type: string;
  name: string;
  description: string;
  level?: number;
}

export interface WafStrategyMappingRow {
  strategy_type: string;
  website_ids: number[];
}

/** GET /waap/domains/:anchorId/strategies — managed WAF strategy catalog. */
export function useActAsWAAPStrategies(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'strategies'),
    enabled: Boolean(anchorId),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<WafStrategyRow[]> => {
      try {
        const res = await actAsClient.get<WafStrategyRow[]>(
          `/waap/domains/${anchorId}/strategies`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/strategies/mapping — applied strategies per website. */
export function useActAsWAAPStrategyMapping(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'strategies/mapping'),
    enabled: Boolean(anchorId),
    staleTime: 2 * 60_000,
    retry: false,
    queryFn: async (): Promise<WafStrategyMappingRow[]> => {
      try {
        const res = await actAsClient.get<WafStrategyMappingRow[]>(
          `/waap/domains/${anchorId}/strategies/mapping`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   AI-WAF Settings (B9 spec) — mode, IP lists, bot policy, account rate limit
   ────────────────────────────────────────────────────────────────────────── */

export interface WaapMode {
  level: number;
}

export interface WaapIPLists {
  blacklist: string[];
}

export interface WaapBotPolicy {
  rule_ids: number[];
}

export interface WaapRateLimit {
  enabled?: boolean;
  rate?: number;
  window?: number;
  action?: string;
  [key: string]: unknown;
}

/** GET /waap/domains/:anchorId/mode — WAF protection level (1-9). */
export function useActAsWAAPMode(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'mode'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapMode | null> => {
      try {
        const res = await actAsClient.get<WaapMode>(
          `/waap/domains/${anchorId}/mode`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/ip-lists — account-level IP block list. */
export function useActAsWAAPIPLists(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'ip-lists'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapIPLists | null> => {
      try {
        const res = await actAsClient.get<WaapIPLists>(
          `/waap/domains/${anchorId}/ip-lists`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/bot-policy — account-level bot rule IDs. */
export function useActAsWAAPBotPolicy(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'bot-policy'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapBotPolicy | null> => {
      try {
        const res = await actAsClient.get<WaapBotPolicy>(
          `/waap/domains/${anchorId}/bot-policy`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/rate-limit — account-level rate limit config. */
export function useActAsWAAPAccountRateLimit(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'rate-limit'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapRateLimit | null> => {
      try {
        const res = await actAsClient.get<WaapRateLimit>(
          `/waap/domains/${anchorId}/rate-limit`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   Emergency Mitigation (B13 spec) — service-scoped
   ────────────────────────────────────────────────────────────────────────── */

/** Scheduling timer type constants (spec: EMERGENCY_SCHEDULING). */
export const EMERGENCY_SCHEDULING = {
  ALWAYS: 'close',
  ONCE: 'once',
  REPEAT: 'repeat',
} as const;

/** Action type constants (spec: EMERGENCY_ACTIONS). */
export const EMERGENCY_ACTIONS = {
  BLOCK_CODE: 'block_code',
  RESPONSE_HTML: 'response_html',
} as const;

/** One sub-strategy row (kill_switch / read_only_website / static_website). */
export interface EmergencyStrategy {
  enable: boolean;
  timer_type: 'close' | 'once' | 'repeat';
  timer_start?: string;
  timer_end?: string;
  timer_once?: string;
  timer_repeat?: string;
  timer_repeat_start?: string;
  timer_repeat_end?: string;
  timer_repeat_days?: number[];
  action_list?: string[];
  sites?: number[];
  service_ids?: number[];
}

export interface EmergencyMitigationConfig {
  kill_switch?: EmergencyStrategy;
  read_only_website?: EmergencyStrategy;
  static_website?: EmergencyStrategy;
}

export interface EmergencyMitigation {
  id?: number | string;
  type?: string;
  config: EmergencyMitigationConfig;
}

/** GET /services/:serviceId/waap/emergency — full emergency config. */
export function useActAsWAAPEmergency(serviceId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.emergency(serviceId ?? ''),
    enabled: Boolean(serviceId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<EmergencyMitigation | null> => {
      try {
        const res = await actAsClient.get<EmergencyMitigation>(
          `/services/${serviceId}/waap/emergency`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   Programmable Mitigation / PBR (B12 spec) — domain-scoped
   ────────────────────────────────────────────────────────────────────────── */

/** PBR path operator options. */
export const PBR_PATH_OPERATORS = ['exact', 'prefix', 'regex'] as const;
export type PbrPathOperator = (typeof PBR_PATH_OPERATORS)[number];

/** PBR key type options. */
export const PBR_KEY_TYPES = ['ip', 'header', 'cookie', 'query', 'ua'] as const;
export type PbrKeyType = (typeof PBR_KEY_TYPES)[number];

export interface PBRWebAccessControlRule {
  id?: number;
  name: string;
  description?: string;
  path_operator: PbrPathOperator;
  path_pattern: string;
  key_type: PbrKeyType;
  key_value?: string[];
  key_regex?: string;
  dev_type?: string[];
  triggering_probability?: number;
  auto_list_key?: boolean;
  auto_list_duration?: number;
  websites?: number[];
}

/** Rate limit window unit options. */
export const PBR_WINDOW_UNITS = ['second', 'minute', 'hour'] as const;
export type PbrWindowUnit = (typeof PBR_WINDOW_UNITS)[number];

export interface PBRRateLimitingRule {
  id?: number;
  name: string;
  description?: string;
  path_operator: PbrPathOperator;
  path_pattern: string;
  key_type: PbrKeyType;
  key_value?: string[];
  window_unit: PbrWindowUnit;
  max_requests: number;
  websites?: number[];
}

/** GET /waap/domains/:anchorId/pbr/web-access-controls — WAC rules list. */
export function useActAsWAAPPBRWebAccessControls(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'pbr/web-access-controls'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<PBRWebAccessControlRule[]> => {
      try {
        const res = await actAsClient.get<PBRWebAccessControlRule[]>(
          `/waap/domains/${anchorId}/pbr/web-access-controls`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/pbr/rate-limitings — Rate Limit rules list. */
export function useActAsWAAPPBRRateLimitings(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'pbr/rate-limitings'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<PBRRateLimitingRule[]> => {
      try {
        const res = await actAsClient.get<PBRRateLimitingRule[]>(
          `/waap/domains/${anchorId}/pbr/rate-limitings`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}
